import { BaseSyntheticEvent, useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import { RootState } from "@redux/stores/store";
import Paper from "@mui/material/Paper";
import { Grid } from "@mui/material";
import { useAppDispatch, useAppSelector } from '@redux/stores/hooks';
import { fetchHelpsAction } from "@redux/actions/help";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import parse from "html-react-parser"

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

export const Help = () => {
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((state: RootState) => state.help);
  const [search, setSearch] = useState<string>('');
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleExpandedChange =
    (panel: string) => (_event: BaseSyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleSearchChange = (event: BaseSyntheticEvent) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    dispatch(fetchHelpsAction());
  }, [dispatch]);

  const filteredAccordionData = list
  .filter((accordion) =>
    accordion.title.toLowerCase().includes(search.toLowerCase()) ||
    accordion.content.toLowerCase().includes(search.toLowerCase())
  )
  .sort((a, b) => {
    if (a.order < b.order) return 1;
    if (a.order > b.order) return -1;
    return a.title.localeCompare(b.title);
  });


  return (
    <Grid sx={{
      width: '100%',
      padding: '10px',
      '& hr': {
        margin: '10px auto',
      },
    }} container spacing={1}>
      <Grid item xs={12}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={70} />
          </Box>
        ) : <Paper sx={{
          padding: '10px',
          display: 'flex',
          overflow: 'auto',
          flexDirection: 'column',
        }}>
          <TextField
            label="Wyszukaj"
            type="search"
            sx={{ mt: 2, mb: 2 }}
            fullWidth
            value={search}
            onChange={handleSearchChange}
          />
          {filteredAccordionData.map((accordion, index) => (
            <Accordion key={accordion._id} expanded={expanded === index.toString()} onChange={handleExpandedChange(index.toString())}>
              <AccordionSummary
                aria-controls={`${accordion.title}-content`}
                id={`${accordion.title}-header`}
              >
                <Typography>{accordion.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  {parse(accordion.content)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>}


      </Grid>
    </Grid>
  )
}